import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ChangeEvent, useState } from "react"
import {
  faDownload,
  faTriangleExclamation,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons"
import { makeStyles, Button, Snackbar, Typography } from "@material-ui/core"
import { useApi } from "../../../utils/API"
import { isEmailValid } from "../../../utils/validators"
import { useUser } from "../../../hooks/useUser"
import { colors } from "../../../utils/theme"
import { Alert } from "@material-ui/lab"

type SafetyPlanCheckListProps = {
  name: string
}

/**
 * Displays an attention banner and card prompting the practitioner to send a
 * Home Safety Checklist to a caregiver. Used when the client has indicated that
 * they may have access to things that pose a risk to their safety.
 *
 * Provides two actions:
 * - **Send via email** — checklist PDF is sent to the entered caregive email.
 *   A generic success or error snackbar is shown based on the response. *Note*:
 *   the API enforces a per-recipient cooldown, so repeated sends to the same user
 *   will display the error message on the snackbar.
 * - **Download PDF** — fetches the checklist PDF blob from the API and triggers
 *   a browser download.
 *
 * @param name - The client's first name, used to personalise the email sent to
 *   the caregiver.
 */
export default function SafetyPlanCheckList({
  name,
}: SafetyPlanCheckListProps) {
  const classes = useStyles()
  const API = useApi()
  const { user } = useUser()
  const [email, setEmail] = useState("")
  const [showEmailError, setShowEmailError] = useState(false)

  const [safetyPlanSent, setSafetyPlanSent] = useState(false)
  const [safetyPlanError, setSafetyPlanError] = useState(false)

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return
    }

    setSafetyPlanSent(false)
  }

  const handleErrorClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return
    }

    setSafetyPlanError(false)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSafetyPlanSent(false)
    setSafetyPlanError(false)
    setEmail(event.target.value)
  }

  const sendEmail = async () => {
    if (!isEmailValid(email)) {
      setShowEmailError(true)
      return
    }

    setShowEmailError(false)
    if (email !== "") {
      const response = await API.emailHomeSafetyChecklist(user.id, email, {
        client_first_name: name,
      })
      if (response.data.success === true) {
        setSafetyPlanSent(true)
      } else {
        setSafetyPlanError(true)
      }
    }
  }

  const downloadPdf = async () => {
    const response = await API.downloadHomeSafetyChecklist()
    const blob = new Blob([response.data], { type: "application/pdf" })
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = blobUrl
    link.download = "Home Safety Checklist.pdf"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  }

  return (
    <div className={classes.sectionContainer}>
      <div className={classes.sectionHeader}>
        <div className={classes.iconContainer}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </div>
        <Typography variant="h5" className={classes.header}>
          Attention
        </Typography>
      </div>

      <div className={classes.content}>
        <Typography className={classes.contentText}>
          This patient has answered “Yes” indicating that they may need support
          staying safe and avoiding items around them that pose a risk of
          suicide or self-harm. <br />
          <br />
          Please consider sending the supplementary
          <b> Home Safety Checklist PDF </b> to a responsible caretaker who can
          provide support.
        </Typography>
        <div className={classes.divider}></div>

        <div id="checklist" className={classes.checklistContainer}>
          <Typography className={classes.checklistText}>
            Send the <b>Home Safety Checklist</b> to a caregiver
          </Typography>
          <div className={classes.checklistSection}>
            <div>
              <input
                type="email"
                id="caregiver-email"
                aria-label="Caregiver email address"
                value={email}
                onChange={handleChange}
                className={classes.txt}
                placeholder="caregiver-email@server.com"
              />
            </div>
            <Button className={classes.textBoxContainer} onClick={sendEmail}>
              <Typography className={classes.btnColor}>Send</Typography>
              <span className={classes.btnColor}>
                <FontAwesomeIcon icon={faPaperPlane} />
              </span>
            </Button>
          </div>
          {showEmailError && (
            <Typography color="error" align="center" role="alert">
              Please enter a valid email.
            </Typography>
          )}
        </div>
        <Snackbar open={safetyPlanSent} onClose={handleClose}>
          <Alert onClose={handleClose} severity="success">
            Email sent to {email}!
          </Alert>
        </Snackbar>
        <Snackbar open={safetyPlanError} onClose={handleErrorClose}>
          <Alert onClose={handleErrorClose} severity="error">
            There was a problem sending the safety checklist to {email}. Please
            try again.
          </Alert>
        </Snackbar>
        <div className={classes.generic}>
          <div className={classes.otherDivider}></div>
          <Typography className={classes.orText}>OR</Typography>
          <div className={classes.otherDivider}></div>
        </div>

        <Button className={classes.downloadButton} onClick={downloadPdf}>
          <FontAwesomeIcon icon={faDownload} className={classes.download} />
          <Typography className={classes.download}>Download PDF</Typography>
        </Button>
      </div>
    </div>
  )
}

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    padding: "16px 12px",
    justifyContent: "space-between",
    alignItems: "center",
    alignSelf: "stretch",
    background: `linear-gradient(0deg, rgba(211, 52, 68, 0.02) 0%, rgba(211, 52, 68, 0.02) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.01) 0%, rgba(0, 0, 0, 0.01) 100%),  ${colors.safetyPlan.cardSecondaryBackground}`,
  },
  iconContainer: {
    display: "flex",
    width: "36px",
    height: "36px",
    minWidth: "36px",
    minHeight: "36px",
    padding: "6.923px 8.308px 9.692px 8.308px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "360px",
    background: colors.safetyPlan.danger,
    flexShrink: 0,
    color: "white",
  },
  header: {
    flex: "1 0 0",
    color: colors.safetyPlan.ink,
    lineHeight: "20px",
    paddingLeft: "8px",
  },
  content: {
    display: "flex",
    padding: "16px 24px 32px 24px",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    alignSelf: "stretch",
    background: colors.safetyPlan.dangerTint,
  },
  contentText: {
    alignSelf: "stretch",
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "20px",
    color: colors.safetyPlan.ink,
  },
  divider: {
    height: "2px",
    alignSelf: "stretch",
    background: colors.safetyPlan.grayscale.black10,
  },
  checklistContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "32px",
  },
  checklistText: {
    color: colors.safetyPlan.ink,
    textAlign: "center",
    fontSize: "18px",
    fontWeight: 500,
    lineHeight: "24px",
  },
  checklistSection: {
    display: "flex",
    alignItems: "flex-start",
    flex: "1 0 0",
    borderRadius: "4px 4px 0px 0px",
  },
  downloadButton: {
    display: "flex",
    height: "32px",
    padding: "4px 20px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "200px",
    background: colors.safetyPlan.danger,
    textTransform: "none",
    "&:hover": {
      background: colors.safetyPlan.danger,
    },
    "& .MuiButton-label": {
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
  },
  download: {
    color: colors.neutral.white,
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "16px",
  },
  otherDivider: {
    height: "2px",
    alignSelf: "stretch",
    width: "50%",
    marginTop: "10px",
    background: colors.safetyPlan.grayscale.black10,
  },
  orText: {
    padding: "0px 20px 0px 20px",
  },
  generic: {
    width: "100%",
    display: "flex",
  },
  textBoxContainer: {
    display: "flex",
    padding: "4px 20px",
    justifyContent: "center",
    alignItems: "center",
    height: "40px",
    borderRadius: "0px 200px 200px 0px",
    border: `2px solid  ${colors.safetyPlan.danger}`,
    textTransform: "none",
    background: colors.safetyPlan.grayscale.white75,
    "& .MuiButton-label": {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  },
  btnColor: {
    color: colors.safetyPlan.danger,
  },
  txt: {
    height: "40px",
    width: "360px",
    fontFamily: "Lato",
    fontSize: "18px",
    paddingLeft: "12px",
    border: `2px solid ${colors.neutral.clay}`,
    color: colors.neutral.asphalt,
    borderRight: "none",
    borderRadius: "4px 0 0 4px",
    outline: "none",
    "&:focus": {
      borderColor: colors.safetyPlan.danger,
    },
  },
  sectionContainer: {
    marginBottom: "20px",
    borderRadius: "8px",
    overflow: "hidden",
  },
  sectionHeader: {
    display: "flex",
    padding: "12px 20px",
    alignItems: "center",
    gap: "10px",
    alignSelf: "stretch",
    borderBottom: `1px solid ${colors.safetyPlan.grayscale.black10}`,
    background: colors.neutral.white,
  },
}))
